import { ChatbotService } from '../../services/chatbot.service';
import {
  ChatMessage,
  ChatbotExtractRequest,
  FollowUpQuestion,
  RecommendedAnswer
} from './chatbot.models';
import {
  buildUserDisplayForExtractRequest,
  createBotMessageFromResponse,
  disableMessageOptions,
  getOutboundTextForFollowUp,
  getOutboundTextForRecommendedAnswer,
  hasExtractRequestPayload,
  unwrapExtractResponse
} from './chatbot-message.util';

/**
 * Shared send/receive logic for ChatbotComponent and ChatbotFabComponent.
 */
export abstract class ChatbotConversationBase {
  messages: ChatMessage[] = [];
  newMessage = '';
  isLoading = false;

  protected abstract getChatbotService(): ChatbotService;

  send(): void {
    const text = this.newMessage.trim();
    if (!text || this.isLoading) {
      return;
    }
    this.newMessage = '';
    this.sendExtractRequest({ text });
  }

  sendText(text: string): void {
    this.sendExtractRequest({ text: (text || '').trim() });
  }

  sendExtractRequest(request: ChatbotExtractRequest, displayLabel?: string): void {
    if (!hasExtractRequestPayload(request) || this.isLoading) {
      return;
    }
    const userLabel = buildUserDisplayForExtractRequest(request, displayLabel);
    disableMessageOptions(this.messages);
    this.messages.push({ role: 'user', content: userLabel });
    this.isLoading = true;

    this.getChatbotService()
      .sendMessage(request)
      .then((response) => {
        const payload = unwrapExtractResponse(response);
        this.messages.push(createBotMessageFromResponse(payload));
        this.isLoading = false;
      })
      .catch((error) => {
        console.error('Chatbot error:', error);
        this.messages.push({
          role: 'bot',
          content:
            'Sorry, I encountered an error while processing your request. Please try again later.',
          optionsDisabled: false
        });
        this.isLoading = false;
      });
  }

  onRecommendedAnswerSelected(option: RecommendedAnswer): void {
    this.sendExtractRequest({ text: getOutboundTextForRecommendedAnswer(option) });
  }

  onFollowUpQuestionSelected(question: FollowUpQuestion): void {
    this.sendExtractRequest({ text: getOutboundTextForFollowUp(question) });
  }

  onExtractInputSelected(request: ChatbotExtractRequest, displayLabel?: string): void {
    this.sendExtractRequest(request, displayLabel);
  }

  clearChat(): void {
    this.messages = [];
    this.newMessage = '';
    this.isLoading = false;
  }
}
